import axios, { AxiosError } from 'axios';
import {
    AdminAuthoriseBatchResultRow,
    AdminEventRequestRow,
    CareHome,
    CareHomeLookupEntry,
    EventRequestErrorBody,
    EventRequestPayload,
    EventRequestSuccess,
    EventTypeOption,
    MyEventCreatePayload,
    MyEventUpdatePayload,
    MyEventsResponse,
    SearchRequest
} from '../types';

const API_BASE_URL = '';

class ApiService {
    private axiosInstance;

    constructor() {
        this.axiosInstance = axios.create({
            baseURL: API_BASE_URL,
            timeout: 10000,
        });
    }

    async searchCareHomes(searchRequest: SearchRequest): Promise<{
        success: boolean;
        data: CareHome[];
        count: number;
        searchCenter: { latitude: number; longitude: number; postcode: string };
    }> {
        try {
            const response = await this.axiosInstance.post('/api/search', searchRequest);
            return response.data;
        } catch (error) {
            console.error('Search API error:', error);
            const axiosErr = error as AxiosError<{ message?: string; error?: string }>;
            const serverMessage = axiosErr.response?.data?.message || axiosErr.response?.data?.error;
            throw new Error(serverMessage || 'Failed to search care homes');
        }
    }

    async searchByCareHomeId(id: string): Promise<{
        success: boolean;
        data: CareHome[];
        count: number;
        searchCenter: { latitude: number; longitude: number; postcode: string };
        radiusMiles: number;
    }> {
        try {
            const response = await this.axiosInstance.post('/api/search', { id });
            return response.data;
        } catch (error) {
            console.error('Search by ID API error:', error);
            throw new Error('Failed to search care homes by ID');
        }
    }

    // section 9 revision 2026-07-14.2 — type-ahead lookup backing the
    // submit-event care home picker. Failures degrade to an empty list.
    async lookupCareHomes(query: string): Promise<CareHomeLookupEntry[]> {
        try {
            const response = await this.axiosInstance.get('/api/carehome-lookup', {
                params: { q: query }
            });
            return response.data?.careHomes || [];
        } catch (error) {
            console.error('Care home lookup API error:', error);
            return [];
        }
    }

    // section 8 revision 2026-07-14.1 — the eventlist endpoint now returns the
    // distinct event types ({ id, label }) rather than the distinct event names.
    async getEventList(): Promise<{ events: EventTypeOption[] }> {
        try {
            const response = await this.axiosInstance.get('/api/eventlist');
            return response.data;
        } catch (error) {
            console.error('Event list API error:', error);
            return { events: [] };
        }
    }

    async healthCheck(): Promise<{ status: string; timestamp: string; service: string }> {
        try {
            const response = await this.axiosInstance.get('/api/health');
            return response.data;
        } catch (error) {
            console.error('Health check API error:', error);
            throw new Error('Health check failed');
        }
    }

    /**
     * Track user action (click on Details, Contact, or Manage links)
     * Fire-and-forget - does not block user navigation
     * @param params Action tracking parameters
     */
    trackUserAction(params: {
        regulator_id: string;
        user_action: 'details' | 'contact' | 'manage';
        user_id: string;
        starting_post_code: string;
        search_position: number;
    }): void {
        // Fire-and-forget - don't await, don't block user interaction
        this.axiosInstance.post('/api/action', params)
            .then(() => {
                console.log(`🖱️ Action tracked: ${params.user_action} for ${params.regulator_id}`);
            })
            .catch((error) => {
                // Log error but don't disrupt user experience
                console.error('Failed to track user action:', error);
            });
    }

    /**
     * Fetch address + lat/lng for an event (used by Add to calendar) and log the
     * add_to_calendar user action server-side. (update 2026_04.22.4)
     */
    /**
     * Submit a public Event-Request (update 2026-05-05.1 / 2026-05-05.2).
     * Resolves with the success body on 200/201, throws an EventRequestApiError
     * carrying the field-level errors and HTTP status on any other outcome so
     * the page can surface them inline.
     */
    async submitEventRequest(payload: EventRequestPayload): Promise<EventRequestSuccess> {
        try {
            const response = await this.axiosInstance.post<EventRequestSuccess>(
                '/api/eventrequest',
                payload
            );
            return response.data;
        } catch (error) {
            const axiosErr = error as AxiosError<EventRequestErrorBody>;
            const status = axiosErr.response?.status ?? 0;
            const body = axiosErr.response?.data;
            const retryAfter = axiosErr.response?.headers?.['retry-after'];
            throw new EventRequestApiError(
                status,
                body?.errors ?? [{ field: 'server', message: axiosErr.message || 'Network error' }],
                retryAfter ? Number(retryAfter) : undefined
            );
        }
    }

    /**
     * Request a passwordless "view my events" sign-in link (update 2026-06-01.1).
     * Always resolves with a generic message (anti-enumeration); throws only on
     * network/validation errors.
     */
    async requestEventLoginLink(email: string): Promise<{ success: boolean; message: string }> {
        try {
            const response = await this.axiosInstance.post('/api/list-events/request', { email });
            return response.data;
        } catch (error) {
            const axiosErr = error as AxiosError<{ errors?: { message: string }[] }>;
            const msg = axiosErr.response?.data?.errors?.[0]?.message || 'Could not send the sign-in link. Please try again.';
            throw new Error(msg);
        }
    }

    /**
     * Exchange a magic-link token for a short-lived session token.
     * (update 2026-06-01.1)
     */
    async verifyEventLoginToken(token: string): Promise<{ success: boolean; sessionToken: string }> {
        try {
            const response = await this.axiosInstance.post('/api/list-events/verify', { token });
            return response.data;
        } catch (error) {
            const axiosErr = error as AxiosError<{ message?: string }>;
            throw new Error(axiosErr.response?.data?.message || 'This sign-in link is invalid or has expired. Please request a new one.');
        }
    }

    /**
     * Fetch the events associated with the signed-in email (update 2026-06-01.1).
     * Requires the session token returned by verifyEventLoginToken.
     */
    async listMyEvents(sessionToken: string): Promise<MyEventsResponse> {
        try {
            const response = await this.axiosInstance.get('/api/list-events', {
                headers: { Authorization: `Bearer ${sessionToken}` }
            });
            return response.data;
        } catch (error) {
            const axiosErr = error as AxiosError<{ message?: string }>;
            throw new Error(axiosErr.response?.data?.message || 'Failed to load your events.');
        }
    }

    /**
     * Consume an emailed event verification link (update 2026-07-06.1).
     * Anonymous submissions verify the email; logged-in creations/edits also
     * authorise the event for display.
     */
    async verifyEventRequestEmail(token: string): Promise<{ success: boolean; email?: string; message: string }> {
        try {
            const response = await this.axiosInstance.post('/api/eventrequest/verify', { token });
            return response.data;
        } catch (error) {
            const axiosErr = error as AxiosError<{ message?: string }>;
            throw new Error(axiosErr.response?.data?.message || 'This verification link is invalid or has expired.');
        }
    }

    /** Create an event for one of the signed-in email's care homes (update 2026-07-06.1). */
    async createMyEvent(sessionToken: string, payload: MyEventCreatePayload): Promise<{ success: boolean; RecordID: string }> {
        try {
            const response = await this.axiosInstance.post('/api/list-events/create', payload, {
                headers: { Authorization: `Bearer ${sessionToken}` }
            });
            return response.data;
        } catch (error) {
            const axiosErr = error as AxiosError<{ message?: string }>;
            throw new Error(axiosErr.response?.data?.message || 'Failed to create the event.');
        }
    }

    /** Edit one of the signed-in email's events (update 2026-07-06.1). */
    async updateMyEvent(sessionToken: string, payload: MyEventUpdatePayload): Promise<{ success: boolean; RecordID: string }> {
        try {
            const response = await this.axiosInstance.post('/api/list-events/update', payload, {
                headers: { Authorization: `Bearer ${sessionToken}` }
            });
            return response.data;
        } catch (error) {
            const axiosErr = error as AxiosError<{ message?: string }>;
            throw new Error(axiosErr.response?.data?.message || 'Failed to update the event.');
        }
    }

    /** Delete one of the signed-in email's events (update 2026-07-06.1). */
    async deleteMyEvent(sessionToken: string, creationKey: string, recordID: string): Promise<{ success: boolean }> {
        try {
            const response = await this.axiosInstance.post('/api/list-events/delete',
                { creationKey, recordID },
                { headers: { Authorization: `Bearer ${sessionToken}` } }
            );
            return response.data;
        } catch (error) {
            const axiosErr = error as AxiosError<{ message?: string }>;
            throw new Error(axiosErr.response?.data?.message || 'Failed to delete the event.');
        }
    }

    /** Re-send the confirmation email for an unverified event (update 2026-07-28.1). */
    async resendMyEventVerification(
        sessionToken: string,
        creationKey: string,
        recordID: string
    ): Promise<{ success: boolean; message: string }> {
        try {
            const response = await this.axiosInstance.post('/api/list-events/resend-verification',
                { creationKey, recordID },
                { headers: { Authorization: `Bearer ${sessionToken}` } }
            );
            return response.data;
        } catch (error) {
            const axiosErr = error as AxiosError<{ message?: string }>;
            throw new Error(axiosErr.response?.data?.message || 'Failed to resend the confirmation email.');
        }
    }

    // --- Autumna staff event management (Entra ID token) (update 2026-07-06.1) ---

    async adminListEventRequests(accessToken: string): Promise<{ success: boolean; requests: AdminEventRequestRow[] }> {
        try {
            const response = await this.axiosInstance.get('/api/eventadmin/requests', {
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            return response.data;
        } catch (error) {
            const axiosErr = error as AxiosError<{ message?: string }>;
            throw new Error(axiosErr.response?.data?.message || 'Failed to load event requests.');
        }
    }

    async adminAuthoriseEventRequest(
        accessToken: string,
        body: { creationKey: string; recordID: string; locationAaId: number; eventTypeId: number }
    ): Promise<{ success: boolean }> {
        try {
            const response = await this.axiosInstance.post('/api/eventadmin/authorise', body, {
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            return response.data;
        } catch (error) {
            const axiosErr = error as AxiosError<{ message?: string }>;
            throw new Error(axiosErr.response?.data?.message || 'Failed to authorise the event request.');
        }
    }

    async adminAuthoriseEventRequestsBatch(
        accessToken: string,
        items: { creationKey: string; recordID: string; locationAaId: number; eventTypeId: number }[]
    ): Promise<{
        success: boolean;
        succeeded: number;
        failed: number;
        results: AdminAuthoriseBatchResultRow[];
    }> {
        try {
            const response = await this.axiosInstance.post(
                '/api/eventadmin/authorise-batch',
                { items },
                { headers: { Authorization: `Bearer ${accessToken}` } }
            );
            return response.data;
        } catch (error) {
            const axiosErr = error as AxiosError<{ message?: string }>;
            throw new Error(axiosErr.response?.data?.message || 'Failed to authorise the selected events.');
        }
    }

    async adminRemoveEventRequest(
        accessToken: string,
        body: { creationKey: string; recordID: string }
    ): Promise<{ success: boolean }> {
        try {
            const response = await this.axiosInstance.post('/api/eventadmin/remove', body, {
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            return response.data;
        } catch (error) {
            const axiosErr = error as AxiosError<{ message?: string }>;
            throw new Error(axiosErr.response?.data?.message || 'Failed to remove the event request.');
        }
    }

    /** Re-send the verification email for an unverified request (update 2026-07-28.1). */
    async adminResendVerification(
        accessToken: string,
        body: { creationKey: string; recordID: string }
    ): Promise<{ success: boolean; message: string }> {
        try {
            const response = await this.axiosInstance.post('/api/eventadmin/resend-verification', body, {
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            return response.data;
        } catch (error) {
            const axiosErr = error as AxiosError<{ message?: string }>;
            throw new Error(axiosErr.response?.data?.message || 'Failed to resend the verification email.');
        }
    }

    async fetchEventCalendarInfo(params: {
        regulator_id: string;
        event_id: string;
        user_id: string;
        browser_session: string;
        starting_post_code?: string;
        search_position?: number;
    }): Promise<{
        success: boolean;
        carehome_name: string;
        address_1: string;
        address_2: string;
        town: string;
        county: string;
        post_code: string;
        latitude: number;
        longitude: number;
    }> {
        try {
            const response = await this.axiosInstance.post('/api/event/calendar', params);
            return response.data;
        } catch (error) {
            console.error('Event calendar API error:', error);
            const axiosErr = error as AxiosError<{ message?: string; error?: string }>;
            const serverMessage = axiosErr.response?.data?.message || axiosErr.response?.data?.error;
            throw new Error(serverMessage || 'Failed to retrieve event calendar info');
        }
    }
}

export class EventRequestApiError extends Error {
    constructor(
        public readonly status: number,
        public readonly errors: { field: string; message: string }[],
        public readonly retryAfterSeconds?: number
    ) {
        super(errors[0]?.message || 'Event request failed');
        this.name = 'EventRequestApiError';
    }
}

export default new ApiService(); 